import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { servicesData } from '../Components/servicesData';
import Header from '../Components/Header';
import MainHeader from '../Components/MainHeader';
import Footer from '../Components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import SEOHelmet from '../Components/SEOHelmet';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { FaCheckCircle, FaStar, FaHeadset, FaPlus, FaMinus } from 'react-icons/fa';
import './ServicePageTemplate.css';

/* Reusable animated stat counter */
const AnimatedStat = ({ value, suffix, label }) => {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
    const isDecimal = value % 1 !== 0;

    return (
        <div className="spt-stat-item" ref={ref}>
            <div className="spt-stat-value">
                {inView ? (
                    <CountUp
                        end={value}
                        duration={2.5}
                        decimals={isDecimal ? 1 : 0}
                        separator=","
                    />
                ) : '0'}
                {suffix}
            </div>
            <div className="spt-stat-label">{label}</div>
        </div>
    );
};

const getStatDesc = (serviceId, idx) => {
    const descMap = {
        'finance-fintech': [
            "No templates. Every line is written securely for your specific financial logic.",
            "Your project stays fully in-house with our vetted, direct development team.",
            "Enterprise-grade AES-256 encryption applied to all data transactions."
        ],
        'website-development': [
            "Handcrafted architectures built for scalability, avoiding bloated CMS templates.",
            "Guaranteed top-tier performance metrics for SEO and user retention.",
            "Free technical support and bug fixes for the first month after going live."
        ],
        'app-development': [
            "We handle store compliance and guarantee your app gets approved.",
            "Transparent sprint-based pricing with complete ownership handover.",
            "Direct communication channels with the engineers building your app."
        ],
        'ui-ux-design': [
            "Every pixel is crafted specifically for your brand identity and audience.",
            "Iterative feedback loops ensuring the final design matches your exact vision.",
            "We balance stunning aesthetics with proven usability principles."
        ],
        'digital-marketing': [
            "Full access to analytics boards and ad-spend tracking at all times.",
            "We focus purely on conversions, ROI, and metrics that actually drive revenue.",
            "Live dashboards available around the clock to track campaign performance."
        ],
        'data-engineering': [
            "Robust data pipelines architected for continuous, failure-resistant ingestion.",
            "Automated failovers and retry mechanisms built into every ETL script.",
            "Your data remains in your VPC. We build the pipes, you keep the keys."
        ],
        'ai-ml': [
            "Models trained in isolated sandboxes. Your proprietary data never leaks.",
            "Transparent model architectures with explainable outputs and biases checked.",
            "Bespoke algorithms designed purely for your unique business dataset."
        ],
        'robotics': [
            "Node scripts and hardware logic tailored exactly to your operational needs.",
            "Thorough virtual testing before any physical hardware is deployed.",
            "Industrial-grade safety and operational redundancy coded into every system."
        ],
        'app-compliance': [
            "We build the most complete, store-ready submission package possible — covering every known review friction point before you submit.",
            "We deploy your full tester ring and generate compliant telemetry within 14 days, guaranteed.",
            "Rejection to resubmission in under 48 hours — our fastest turnaround on Apple and Google Play remediations."
        ]
    };
    return (descMap[serviceId] && descMap[serviceId][idx]) || "Core guarantee of service quality and reliability.";
};

/* Helper to get service-specific trust points */
const getTrustPoints = (serviceId) => {
    const pointMap = {
        'finance-fintech': ["PCI-DSS Security Compliance", "End-to-End Encryption Setup", "Secure Gateway Sandbox"],
        'website-development': ["Complete Source Code Handoff", "Lighthouse Score > 90 Optimized", "30-Day Launch Maintenance"],
        'app-development': ["App Store Submission Support", "Offline-First Sync Solutions", "Cross-Platform Reuse Optimization"],
        'ui-ux-design': ["Figma Design System Sources", "Atomic UI Standard Components", "User Journey Flow Charts"],
        'digital-marketing': ["Full Campaign Board Access", "A/B Checkout Testing Logs", "Keyword & Competitor Audits"],
        'data-engineering': ["ETL Custom Runbook Handover", "Scalable Data Cloud schemas", "Pipeline Failure Alert Setup"],
        'ai-ml': ["Custom Model Weights Transfer", "Automated Input Drift Logs", "Secure VPC Data Sandboxes"],
        'robotics': ["ROS-Compliant Node Scripts", "3D Virtual Simulator Tests", "Hardware Wiring Schematics"],
        'app-compliance': ["Deep Pre-Submission Audit & Checklist", "Vetted Real-Device Tester Network", "NDA-Protected Developer Console Access"]
    };
    return pointMap[serviceId] || ["Complete Source Handoff", "Enterprise-Grade Performance", "30-Day Launch Support"];
};

/* Process steps — shared across all services */
const processSteps = [
    { num: '01', title: 'Discovery & Research', desc: 'Deep-dive into your business, audience, and competitive landscape to uncover strategic opportunities.' },
    { num: '02', title: 'Strategy & Planning', desc: 'Define roadmaps, technical architecture, and milestones with complete transparency.' },
    { num: '03', title: 'Design & Prototyping', desc: 'Craft high-fidelity designs and interactive prototypes for early validation and feedback.' },
    { num: '04', title: 'Development & Testing', desc: 'Agile engineering with continuous integration, code reviews, and rigorous QA at every sprint.' },
    { num: '05', title: 'Launch & Support', desc: 'Seamless deployment followed by 24/7 monitoring, performance tuning, and iterative improvements.' },
];

const ServicePageTemplate = () => {
    const { serviceId } = useParams();
    const service = servicesData[serviceId];
    const [activeFaq, setActiveFaq] = useState(null);

    useEffect(() => {
        AOS.init({ offset: 100, duration: 800, once: true });
        window.scrollTo(0, 0);
    }, [serviceId]);

    if (!service) {
        return (
            <div className="spt-error-page">
                <h2>Service Not Found</h2>
                <NavLink to="/" className="spt-btn-home">Go back to Home</NavLink>
            </div>
        );
    }

    const isCompliance = serviceId === 'app-compliance';

    // --- Compliance-specific SEO values ---
    const complianceSeoTitle = "Google Play & App Store Publishing | Compliance Services, Closed Testing & Rejection Fix – Octalbees";
    const complianceSeoDesc = "Struggling with Google Play's 20-tester requirement or Apple rejections? Octalbees deploys real-device testers, runs pre-submission audits, and handles rejection appeals. Starting ₹2,499.";

    // --- FAQ Schema (Google rich results — FAQ accordion in search) ---
    const faqSchema = service.faqs ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": service.faqs.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
            }
        }))
    } : null;

    // --- Offer / Pricing Schema ---
    const offerSchema = isCompliance && service.pricingTiers ? {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "App Store Publishing & Compliance",
        "description": complianceSeoDesc,
        "provider": {
            "@type": "Organization",
            "name": "Octalbees",
            "url": "https://www.octalbees.com"
        },
        "areaServed": "IN",
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Compliance Service Plans",
            "itemListElement": service.pricingTiers.map((tier, i) => ({
                "@type": "Offer",
                "position": i + 1,
                "name": tier.name,
                "description": tier.tagline,
                "price": tier.price.replace(/[₹,]/g, ''),
                "priceCurrency": "INR",
                "url": "https://www.octalbees.com/services/app-compliance"
            }))
        }
    } : null;

    const serviceSchema = isCompliance ? offerSchema : {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": service.heroTitle,
        "description": service.introText,
        "serviceType": service.heroTitle,
        "areaServed": "Worldwide",
        "provider": {
            "@type": "Organization",
            "name": "Octalbees",
            "url": "https://www.octalbees.com"
        },
        "url": `https://www.octalbees.com/services/${serviceId}`
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.octalbees.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.octalbees.com/services" },
            { "@type": "ListItem", "position": 3, "name": service.heroTitle, "item": `https://www.octalbees.com/services/${serviceId}` }
        ]
    };

    const seoTitle = service.seoTitle || (isCompliance ? complianceSeoTitle : `${service.heroTitle} Services`);
    const seoDesc  = service.seoDescription || (isCompliance ? complianceSeoDesc  : service.introText.substring(0, 155));


    return (
        <div className="ServicePage premium-dark-theme">
            <SEOHelmet
                title={seoTitle}
                description={seoDesc}
                path={`/services/${serviceId}`}
                jsonLd={serviceSchema}
                breadcrumbSchema={breadcrumbSchema}
                extraJsonLd={faqSchema}
            />
            <Header />
            <MainHeader />

            {/* --- Ambient Background Orbs --- */}
            {/* Removed Orbs as per user request */}

            {/* =============================================
                SECTION 1: CINEMATIC HERO
                ============================================= */}
            <section className="spt-hero" id="service-hero">
                <div className="spt-hero-grid"></div>

                {/* Floating geometric shapes */}
                <div className="spt-hero-shape spt-shape-1"></div>
                <div className="spt-hero-shape spt-shape-2"></div>
                <div className="spt-hero-shape spt-shape-3"></div>
                <div className="spt-hero-shape spt-shape-4"></div>

                <div className="container sp-z-2 text-center">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            {/* Breadcrumb */}
                            <nav className="spt-breadcrumb">
                                <NavLink to="/">Home</NavLink>
                                <span className="spt-breadcrumb-sep">›</span>
                                <NavLink to="/services">Services</NavLink>
                                <span className="spt-breadcrumb-sep">›</span>
                                <span className="spt-breadcrumb-current">{service.heroTitle}</span>
                            </nav>

                            {/* Badge */}
                            <div className="spt-hero-badge">
                                {isCompliance ? 'SKIP THE REJECTION' : 'PREMIUM SOLUTIONS'}
                            </div>

                            {/* Title */}
                            <h1 className="spt-hero-title">
                                {service.heroTitle}
                            </h1>

                            {/* Subtitle */}
                            <p className="spt-hero-subtitle text-center">
                                {service.heroSubtitle}
                            </p>

                            <div className="spt-hero-ctas mt-4">
                                <NavLink to="/contact-us" className="spt-cta-primary">
                                    Get a Free Quote <span className="spt-arrow">→</span>
                                </NavLink>
                                <a href="#service-process" className="spt-cta-secondary ms-sm-3 mt-3 mt-sm-0">
                                    View Our Process
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* =============================================
                SECTION 2: BENTO VALUE DASHBOARD (Redesigned)
                ============================================= */}
            <section className="spt-value" id="service-value">
                <div className="container">
                    <div className="row g-4 align-items-stretch justify-content-center">
                        {/* Left Column: Glass Console */}
                        <div className="col-lg-7 d-flex">
                            <div className="spt-value-glass w-100">
                                <div className="spt-value-overline">{service.introHeading}</div>
                                <h2 className="spt-value-heading">
                                    Why {service.heroTitle}?
                                </h2>
                                <p className="spt-value-text">{service.introText}</p>
                                
                                <div className="spt-value-trust-checklist">
                                    {getTrustPoints(serviceId).map((point, idx) => (
                                        <div className="spt-trust-point" key={idx}>
                                            <FaCheckCircle className="spt-trust-point-icon" />
                                            <span>{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Metrics Stack */}
                        <div className="col-lg-5">
                            <div className="spt-value-metrics-stack">
                                {service.stats && service.stats.map((stat, idx) => (
                                    <div 
                                        className="spt-value-metric-card" 
                                        key={idx}
                                    >
                                        <div className="spt-value-metric-header">
                                            {stat.displayText ? (
                                                <div className="spt-stat-value">{stat.displayText}</div>
                                            ) : (
                                                <AnimatedStat 
                                                    value={stat.value} 
                                                    suffix={stat.suffix} 
                                                    label="" 
                                                />
                                            )}
                                        </div>
                                        <div className="spt-value-metric-body">
                                            <h4>{stat.label}</h4>
                                            <p>{getStatDesc(serviceId, idx)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* =============================================
                SECTION 3: TECH STACK
                ============================================= */}
            {service.technologies && (
                <section className="spt-tech" id="service-tech">
                    <div className="container sp-z-2">
                        <div className="text-center mb-5">
                            <p className="spt-section-overline">POWERED BY</p>
                            <h2 className="spt-section-heading">
                                Technological Foundation
                            </h2>
                            <div className="spt-heading-divider"></div>
                        </div>
                        <div className="spt-tech-grid">
                            {service.technologies.map((tech, idx) => (
                                <div
                                    className="spt-tech-card"
                                    key={idx}
                                >
                                    <div className="spt-tech-icon">{tech.icon}</div>
                                    <span className="spt-tech-name">{tech.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* =============================================
                SECTION 3.5: INTEGRATIONS & PLATFORMS
                ============================================= */}
            {service.integrations && (
                <section className="spt-integrations" id="service-integrations">
                    <div className="container sp-z-2">
                        <div className="text-center mb-5">
                            <p className="spt-section-overline">COMPATIBILITY</p>
                            <h3 className="spt-integrations-heading">
                                Seamless Integrations & Platforms
                            </h3>
                            <div className="spt-heading-divider"></div>
                            <p className="spt-integrations-subheading text-center mx-auto">
                                Engineered to connect and collaborate with industry-standard frameworks, security layers, and API infrastructures.
                            </p>
                        </div>
                        <div className="spt-integrations-grid">
                            {service.integrations.map((item, idx) => (
                                <div className="spt-integration-badge" key={idx}>
                                    <span className="spt-integration-dot"></span>
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* =============================================
                SECTION 4: BENTO GRID CAPABILITIES
                ============================================= */}
            <section className="spt-capabilities" id="service-capabilities">
                <div className="container sp-z-2">
                    <div className="text-center mb-5">
                        <h2 className="spt-section-heading">Core Capabilities</h2>
                        <div className="spt-heading-divider"></div>
                    </div>

                    <div className="spt-bento-grid">
                        {service.subServices.map((sub, index) => (
                            <div
                                className={`spt-bento-card${index === 0 ? ' spt-bento-featured' : ''}`}
                                key={index}
                            >
                                <div className="spt-bento-border"></div>
                                <span className="spt-bento-number">
                                    {String(index + 1).padStart(2, '0')}
                                </span>
                                <div className="spt-bento-content">
                                    <div className="spt-bento-icon-wrap">{sub.icon}</div>
                                    <h4 className="spt-bento-title">{sub.title}</h4>
                                    <p className="spt-bento-desc text-center">{sub.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* =============================================
                SECTION 4.5: PRICING TIERS (compliance only)
                ============================================= */}
            {serviceId === 'app-compliance' && service.pricingTiers && (
                <section className="spt-pricing" id="service-pricing">
                    <div className="container sp-z-2">
                        <div className="text-center mb-5">
                            <p className="spt-section-overline">TRANSPARENT PRICING</p>
                            <h2 className="spt-section-heading">Choose Your Protection Plan</h2>
                            <div className="spt-heading-divider"></div>
                            <p className="spt-pricing-subtext text-center mx-auto">
                                No hidden fees. No lock-in contracts. Pay for exactly what your app needs.
                            </p>
                        </div>
                        <div className="spt-pricing-grid">
                            {service.pricingTiers.map((tier, idx) => {
                                const waNumber = "919470506529";
                                const waMessage = `Hi Octalbees! 👋\n\nI'm interested in the *${tier.name}* plan (${tier.price}) for App Store Publishing & Compliance.\n\n*What's included:*\n${tier.features.map(f => `• ${f}`).join('\n')}\n\nCould you help me get started?`;
                                const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;
                                return (
                                <div className={`spt-pricing-card${tier.badge === 'Most Popular' ? ' spt-pricing-featured' : ''}`} key={idx}>
                                    {tier.badge && (
                                        <div className="spt-pricing-badge">{tier.badge}</div>
                                    )}
                                    <div className="spt-pricing-header">
                                        <h3 className="spt-pricing-name">{tier.name}</h3>
                                        <p className="spt-pricing-tagline">{tier.tagline}</p>
                                        <div className="spt-pricing-price-wrap">
                                            <span className="spt-pricing-note">{tier.priceNote}</span>
                                            <span className="spt-pricing-price">{tier.price}</span>
                                        </div>
                                    </div>
                                    <ul className="spt-pricing-features">
                                        {tier.features.map((f, fi) => (
                                            <li key={fi}>
                                                <span className="spt-pricing-check">✓</span>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`spt-pricing-cta${tier.badge === 'Most Popular' ? ' spt-pricing-cta-featured' : ''}`}
                                    >
                                        <span className="spt-pricing-cta-wa-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{marginRight: '8px', verticalAlign: 'middle'}}>
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                        </span>
                                        {tier.cta}
                                    </a>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* =============================================
                SECTION 4.7: CASE STUDIES (compliance only)
                ============================================= */}
            {serviceId === 'app-compliance' && service.caseStudies && (
                <section className="spt-cases" id="service-cases">
                    <div className="container sp-z-2">
                        <div className="text-center mb-5">
                            <p className="spt-section-overline">REAL RESULTS</p>
                            <h2 className="spt-section-heading">What We've Solved</h2>
                            <div className="spt-heading-divider"></div>
                        </div>
                        <div className="spt-cases-grid">
                            {service.caseStudies.map((cs, idx) => (
                                <div className="spt-case-card" key={idx}>
                                    <div className="spt-case-header">
                                        <div className="spt-case-platform-tags">
                                            <span className={`spt-case-platform spt-platform-${cs.platform === 'Google Play' ? 'play' : 'apple'}`}>
                                                {cs.platform}
                                            </span>
                                            <span className="spt-case-category">{cs.category}</span>
                                        </div>
                                        <div className="spt-case-metric-wrap">
                                            <span className="spt-case-metric">{cs.metric}</span>
                                            <span className="spt-case-metric-label">{cs.metricLabel}</span>
                                        </div>
                                    </div>
                                    <h3 className="spt-case-title">{cs.title}</h3>
                                    <div className="spt-case-body">
                                        <div className="spt-case-block">
                                            <span className="spt-case-block-label">The Problem</span>
                                            <p>{cs.problem}</p>
                                        </div>
                                        <div className="spt-case-block">
                                            <span className="spt-case-block-label">How We Fixed It</span>
                                            <p>{cs.solution}</p>
                                        </div>
                                        <div className="spt-case-result">
                                            <span className="spt-case-result-icon">✓</span>
                                            <span>{cs.result}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* =============================================
                SECTION 5: PROCESS TIMELINE
                ============================================= */}
            <section className="spt-process" id="service-process">
                <div className="container sp-z-2">
                    <div className="text-center mb-5">
                        <p className="spt-section-overline">OUR APPROACH</p>
                        <h2 className="spt-section-heading">
                            How We Deliver Excellence
                        </h2>
                        <div className="spt-heading-divider"></div>
                    </div>

                    <div className="spt-timeline">
                        {processSteps.map((step, idx) => (
                            <div
                                className="spt-timeline-item"
                                key={idx}
                                data-aos="fade-up"
                            >
                                <div className="spt-timeline-content">
                                    <div className="spt-timeline-card">
                                        <h4 className="spt-timeline-title">{step.title}</h4>
                                        <p className="spt-timeline-desc">{step.desc}</p>
                                    </div>
                                </div>
                                <div className="spt-timeline-dot">{step.num}</div>
                                <div className="spt-timeline-content" style={{ visibility: 'hidden' }}>
                                    {/* Spacer for alternating layout */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* =============================================
                SECTION 5.5: PROJECT DELIVERABLES TIMELINE
                ============================================= */}
            {service.deliverables && (
                <section className="spt-deliverables" id="service-deliverables">
                    <div className="container sp-z-2">
                        <div className="text-center mb-5">
                            <p className="spt-section-overline">TRANSPARENCY</p>
                            <h2 className="spt-section-heading">
                                What You Receive At Each Stage
                            </h2>
                            <div className="spt-heading-divider"></div>
                        </div>

                        <div className="spt-deliverables-grid">
                            {service.deliverables.map((item, idx) => (
                                <div 
                                    className="spt-deliverable-card" 
                                    key={idx}
                                >
                                    <div className="spt-deliverable-header">
                                        <span className="spt-deliverable-num">{item.stage}</span>
                                        <h4 className="spt-deliverable-title">{item.title}</h4>
                                    </div>
                                    <div className="spt-deliverable-body">
                                        <h5 className="spt-deliverable-item">{item.item}</h5>
                                        <p className="spt-deliverable-desc">{item.desc}</p>
                                    </div>
                                    <div className="spt-deliverable-glow-border"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* =============================================
                SECTION 5.8: FREQUENTLY ASKED QUESTIONS
                ============================================= */}
            {service.faqs && (
                <section className="spt-faqs" id="service-faqs">
                    <div className="container sp-z-2">
                        <div className="text-center mb-5">
                            <p className="spt-section-overline">QUESTIONS & ANSWERS</p>
                            <h2 className="spt-section-heading">
                                Frequently Asked Questions
                            </h2>
                            <div className="spt-heading-divider"></div>
                        </div>

                        <div className="spt-faqs-container">
                            {service.faqs.map((faq, idx) => {
                                const isOpen = activeFaq === idx;
                                return (
                                    <div 
                                        className={`spt-faq-item ${isOpen ? 'spt-faq-open' : ''}`} 
                                        key={idx}
                                    >
                                        <button 
                                            className="spt-faq-question" 
                                            onClick={() => setActiveFaq(isOpen ? null : idx)}
                                            aria-expanded={isOpen}
                                        >
                                            <span>{faq.q}</span>
                                            <span className="spt-faq-icon">
                                                {isOpen ? <FaMinus /> : <FaPlus />}
                                            </span>
                                        </button>
                                        <div className="spt-faq-answer">
                                            <div className="spt-faq-answer-inner">
                                                <p>{faq.a}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* =============================================
                SECTION 6: IMMERSIVE CTA
                ============================================= */}
            <section className="spt-cta" id="service-cta">
                <div className="spt-cta-radial"></div>

                {/* Decorative rings */}
                <div className="spt-cta-ring spt-cta-ring-1"></div>
                <div className="spt-cta-ring spt-cta-ring-2"></div>
                <div className="spt-cta-ring spt-cta-ring-3"></div>

                <div className="container text-center sp-z-2">
                    <h2 className="spt-cta-heading">
                        {service.ctaHeading || "Ready to Build Something Extraordinary?"}
                    </h2>
                    <p className="spt-cta-subtext text-center">
                        {service.ctaSubtext || "Partner with us to transform your vision into reality. Let's create something the world hasn't seen yet."}
                    </p>

                    <div className="spt-cta-buttons">
                        <NavLink
                            to="/contact-us"
                            className="spt-cta-primary"
                            onClick={() => window.scrollTo(0, 0)}
                        >
                            Start Your Project <span className="spt-arrow">→</span>
                        </NavLink>
                        <a
                            href={`https://wa.me/919470506529?text=Hi! I'm interested in your ${service.heroTitle} service. Could we discuss my project?`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="spt-cta-secondary"
                        >
                            Chat on WhatsApp
                        </a>
                        <a
                            href="mailto:info@octalbees.com"
                            className="spt-cta-secondary"
                        >
                            Email Us
                        </a>
                    </div>

                    {/* Trust badges */}
                    <div className="spt-trust-row">
                        <div className="spt-trust-badge">
                            <FaCheckCircle className="spt-trust-badge-icon" />
                            <span>100+ Projects Delivered</span>
                        </div>
                        <div className="spt-trust-badge">
                            <FaStar className="spt-trust-badge-icon" />
                            <span>5★ Client Rated</span>
                        </div>
                        <div className="spt-trust-badge">
                            <FaHeadset className="spt-trust-badge-icon" />
                            <span>24/7 Dedicated Support</span>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServicePageTemplate;